import React from 'react';
import { PageBanner } from '../components/PageBanner';
import { ContactCTA } from '../components/ContactCTA';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { UserIcon, ShieldCheckIcon, HeartIcon, SparklesIcon, CheckCircleIcon, SmileIcon } from 'lucide-react';
export function PersonalCarePage() {
  const {
    ref: introRef,
    isVisible: isIntroVisible
  } = useScrollAnimation();
  const {
    ref: benefitsRef,
    isVisible: isBenefitsVisible
  } = useScrollAnimation();
  const {
    ref: detailsRef,
    isVisible: isDetailsVisible
  } = useScrollAnimation();
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useScrollAnimation();
  return <main data-id="element-982">
      <PageBanner title="Personal Care Services" subtitle="Compassionate assistance with daily living to maintain dignity and independence." breadcrumbs={[{
      label: 'Services',
      path: '/services'
    }, {
      label: 'Personal Care',
      path: '/services/personal-care'
    }]} data-id="element-983" />

      {/* Intro Section */}
      <section className="py-16 md:py-20 bg-light-alt shadow-inner" data-id="element-984">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-985">
          <div ref={introRef} className={`transition-all duration-1000 ease-out ${isIntroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-986">
            <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6" data-id="element-987">
              <UserIcon className="h-4 w-4 text-primary" data-id="element-988" />
              <span className="text-primary text-sm font-semibold" data-id="element-989">
                Daily Living Support
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6 leading-tight" data-id="element-990">
              Dignified Support for Your Daily Routine
            </h2>
            <div className="space-y-5 text-dark-muted text-lg leading-relaxed" data-id="element-991">
              <p data-id="element-992">
                Our personal care services provide essential support for
                individuals who need assistance with the activities of daily
                living. We focus on providing this care with the utmost respect,
                ensuring our clients maintain their dignity and feel comfortable
                in their own homes.
              </p>
              <p data-id="element-993">
                Whether it's help with morning routines, hygiene, or mobility
                throughout the day, our compassionate caregivers are here to
                provide a helping hand while encouraging as much independence as
                possible.
              </p>
            </div>

            {/* RN Badge */}
            <div className="mt-8 flex items-center bg-primary/5 border border-primary/10 rounded-xl p-5" data-id="element-994">
              <ShieldCheckIcon className="h-10 w-10 text-primary mr-4 flex-shrink-0" data-id="element-995" />
              <div data-id="element-996">
                <h4 className="font-bold text-dark" data-id="element-997">RN Supervised Care</h4>
                <p className="text-sm text-dark-muted" data-id="element-998">
                  All personal care plans are developed and supervised by
                  Registered Nurses to ensure safety and quality.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-light shadow-inner" data-id="element-999">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1000">
          <div ref={benefitsRef} className={`transition-all duration-1000 ease-out ${isBenefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-1001">
            <div className="text-center mb-14" data-id="element-1002">
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4" data-id="element-1003">
                Why Choose Our Personal Care
              </h2>
              <div className="w-16 h-1.5 bg-secondary mx-auto mb-6 rounded-full" data-id="element-1004"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1005">
              {[{
              icon: SmileIcon,
              title: 'Dignity First',
              desc: 'We prioritize respect and privacy in every aspect of personal assistance.'
            }, {
              icon: HeartIcon,
              title: 'Compassionate Staff',
              desc: 'Caregivers selected for their empathy and dedication to patient well-being.'
            }, {
              icon: SparklesIcon,
              title: 'Hygiene & Health',
              desc: 'Maintaining cleanliness to promote physical health and emotional comfort.'
            }, {
              icon: ShieldCheckIcon,
              title: 'Safety Focused',
              desc: 'Assistance with transfers and movement to prevent accidents at home.'
            }].map((benefit, idx) => <div key={idx} className="bg-light-alt rounded-2xl p-7 shadow-card hover:shadow-card-hover border border-gray-200 transition-all duration-300 transform hover:-translate-y-1 group" data-id="element-1006">
                  <div className="bg-primary/10 w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary transition-colors duration-300" data-id="element-1007">
                    <benefit.icon className="h-7 w-7 text-primary group-hover:text-white transition-colors duration-300" data-id="element-1008" />
                  </div>
                  <h3 className="text-lg font-bold text-dark mb-3" data-id="element-1009">
                    {benefit.title}
                  </h3>
                  <p className="text-dark-muted text-sm leading-relaxed" data-id="element-1010">
                    {benefit.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className="py-16 md:py-20 bg-light-alt shadow-inner" data-id="element-1011">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1012">
          <div ref={detailsRef} className={`transition-all duration-1000 ease-out ${isDetailsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-1013">
            <h2 className="text-3xl font-bold text-dark mb-6" data-id="element-1014">
              Our Personal Care Services Include
            </h2>
            <p className="text-dark-muted text-lg leading-relaxed mb-8" data-id="element-1015">
              We offer a wide range of assistance tailored to the specific needs
              and preferences of each individual we serve.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1016">
              {['Bathing and showering assistance', 'Grooming and dressing', 'Oral hygiene and skin care', 'Incontinence care and toileting', 'Mobility and transfer assistance', 'Feeding assistance', 'Positioning and turning', 'Morning and evening routines'].map((item, idx) => <div key={idx} className="flex items-center p-4 bg-light rounded-xl border border-gray-200" data-id="element-1017">
                  <CheckCircleIcon className="h-6 w-6 text-secondary mr-3 flex-shrink-0" data-id="element-1018" />
                  <span className="text-dark font-medium" data-id="element-1019">{item}</span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-light" data-id="element-1020">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1021">
          <div ref={ctaRef} className={`bg-primary rounded-3xl p-10 md:p-14 text-center shadow-xl relative overflow-hidden transition-all duration-1000 ease-out ${isCtaVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'}`} data-id="element-1022">
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(#ffffff 2px, transparent 2px)',
            backgroundSize: '30px 30px'
          }} data-id="element-1023"></div>
            <div className="relative z-10" data-id="element-1024">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{
              fontFamily: "'Cinzel', serif"
            }} data-id="element-1025">
                Need Personalized Support?
              </h2>
              <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-1026">
                Contact us today to discuss your personal care needs and learn
                how we can support your daily routine.
              </p>
              <a href="#/contact" className="inline-block bg-white text-primary-dark hover:bg-light-alt px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1" data-id="element-1027">
                Contact Us Today
              </a>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA data-id="element-1028" />
    </main>;
}